import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Camera, StickyNote, CheckSquare, Receipt, FileEdit, FileText, FileCheck,
         Calendar, Mic, X, Loader2, Building2 } from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";
import { useAuth } from "@/contexts/AuthContext";

// Batch 1 — one big + button, one way to add anything. Plain words, one main
// action per screen, current job remembered (3 most recent jobs offered first).
// Talk: 360 Assistant parses the voice note, shows a preview card, and saves
// ONLY after the Save tap — never before.

const KIND_ICON = { photo: Camera, note: StickyNote, task: CheckSquare, receipt: Receipt,
  change_order: FileEdit, estimate: FileText, invoice: FileCheck, schedule: Calendar };

import { useNearbyJob } from "@/lib/nearbyJob";

export default function QuickAddSheet({ open, onClose, onCamera, modOn }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const owner = user?.role !== "crew";
  const [projects, setProjects] = useState([]);
  const [picked, setPicked] = useState(null); // explicit user pick
  const [talk, setTalk] = useState(false);
  const [heard, setHeard] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  // Current job comes from the URL when the user is standing on a job page.
  const currentId = useMemo(() => {
    const m = loc.pathname.match(/^\/app\/projects\/([^/]+)/);
    return m ? m[1] : "";
  }, [loc.pathname]);

  useEffect(() => {
    if (!open) return;
    api.get("/projects").then(({ data }) => {
      const items = (Array.isArray(data) ? data : data.items || [])
        .filter((p) => !p.deleted_at && p.status !== "archived")
        .sort((a, b) => (b.last_activity_at || "").localeCompare(a.last_activity_at || ""));
      setProjects(items);
    }).catch(() => setProjects([]));
  }, [open]);

  // "You're here": default to the job the user is standing at (one tap to change).
  const nearbyOn = (user?.notif_prefs?.nearby_first) !== false;
  const nearby = useNearbyJob(open ? projects : [], nearbyOn && open);
  const projectId = picked || currentId || nearby?.project?.id || "";
  const recent = useMemo(() => {
    const cur = projects.find((p) => p.id === projectId);
    const rest = projects.filter((p) => p.id !== projectId).slice(0, 3);
    return cur ? [cur, ...rest] : rest;
  }, [projects, projectId]);
  const projName = (id) => projects.find((p) => p.id === id)?.name || "";

  const go = (path) => { onClose(); navigate(path); };
  const needJob = !projectId;

  const actions = [
    { key: "photo", icon: Camera, testid: "quick-add-photo", onClick: () => { onClose(); onCamera?.(); } },
    { key: "note", icon: StickyNote, testid: "quick-add-note", disabled: needJob,
      onClick: () => { setTalk(false); setDraft({ kind: "note", project_id: projectId, title: "" }); } },
    { key: "task", icon: CheckSquare, testid: "quick-add-task", disabled: needJob,
      onClick: () => { setTalk(false); setDraft({ kind: "task", project_id: projectId, title: "" }); } },
    { key: "receipt", icon: Receipt, testid: "quick-add-receipt", disabled: needJob,
      onClick: () => go(`/app/projects/${projectId}#pd-jobcosts`) },
    { key: "change_order", icon: FileEdit, testid: "quick-add-co", disabled: needJob || !owner, ownerOnly: true,
      onClick: () => go(`/app/projects/${projectId}/change-order/new`) },
    { key: "estimate", icon: FileText, testid: "quick-add-estimate", ownerOnly: true,
      onClick: () => go(`/app/financials/estimate/new${projectId ? `?project=${projectId}` : ""}`) },
    { key: "invoice", icon: FileCheck, testid: "quick-add-invoice", ownerOnly: true,
      onClick: () => go(`/app/financials/invoice/new${projectId ? `?project=${projectId}` : ""}`) },
    { key: "schedule", icon: Calendar, testid: "quick-add-schedule",
      onClick: () => go(`/app/schedule${projectId ? `?project=${projectId}` : ""}`) },
  ].filter((a) => owner || !a.ownerOnly);

  const parseVoice = async (text) => {
    setHeard(text);
    setBusy(true);
    try {
      const { data } = await api.post("/quick-add/parse", {
        text, current_project_id: projectId || undefined,
        projects: recent.map((p) => ({ id: p.id, name: p.name })),
      });
      setDraft(data);
      if (data.project_id && data.project_id !== projectId) setPicked(data.project_id);
    } catch { toast.error(t("toast_failed")); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!draft?.project_id) { toast.error(t("qa_choose_job")); return; }
    setBusy(true);
    try {
      if (draft.kind === "note") {
        await api.post("/logs", { project_id: draft.project_id, text: draft.title, lang: (i18n.language || "en").slice(0, 2) });
        // V1: "Crew of 3, 7am-3:30pm" → one-tap draft timecard (owner approves in Time review)
        const hm = draft.title.match(/(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?\s*[-–—]\s*(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)/i);
        const cm = draft.title.match(/crew of (\d+)/i);
        if (hm) {
          const toH = (h, m, ap) => { let x = parseInt(h, 10) % 12; if (ap && ap.toLowerCase().startsWith("p")) x += 12; return x + (parseInt(m || "0", 10) / 60); };
          let hrs = toH(hm[4], hm[5], hm[6]) - toH(hm[1], hm[2], hm[3] || hm[6]);
          if (hrs <= 0) hrs += 12;
          hrs = Math.round(hrs * 100) / 100;
          const crew = cm ? parseInt(cm[1], 10) : 1;
          const pid2 = draft.project_id, txt = draft.title;
          toast(t("log_hours_offer", { hours: hrs, crew }), {
            duration: 12000,
            action: { label: t("log_hours_yes"), onClick: async () => {
              try { await api.post("/time/manual-entry", { project_id: pid2, hours: hrs * crew, crew_count: crew, text: txt }); toast.success(t("log_hours_added")); }
              catch { toast.error(t("toast_failed")); }
            } },
          });
        }
      } else if (draft.kind === "task") {
        await api.post("/tasks", { project_id: draft.project_id, title: draft.title, due_date: draft.due_date || undefined });
      } else {
        // Money / schedule kinds open the real editor with the job prefilled.
        const pid = draft.project_id;
        const dest = { photo: `/app/projects/${pid}`, receipt: `/app/projects/${pid}#pd-jobcosts`,
          change_order: `/app/projects/${pid}/change-order/new`,
          estimate: `/app/financials/estimate/new?project=${pid}`,
          invoice: `/app/financials/invoice/new?project=${pid}`,
          schedule: `/app/schedule?project=${pid}` }[draft.kind];
        onClose();
        if (draft.kind === "photo") onCamera?.();
        else navigate(dest);
        return;
      }
      toast.success(t("toast_saved"));
      setDraft(null); setHeard(""); setTalk(false);
      onClose();
    } catch { toast.error(t("toast_failed")); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  const DraftIcon = draft ? (KIND_ICON[draft.kind] || StickyNote) : null;

  return createPortal(
    <div className="fixed inset-0 z-[120]" data-testid="quick-add-sheet" role="dialog" aria-modal="true" aria-label={t("qa_title")}>
      <div className="absolute inset-0 bg-black/60" onClick={onClose} data-testid="quick-add-backdrop" />
      <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white border-t border-slate-200 p-4 pb-[max(env(safe-area-inset-bottom,8px),16px)] max-h-[85vh] overflow-y-auto">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300" />
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-lg font-extrabold text-slate-900">{t("qa_title")}</h2>
          <button onClick={onClose} data-testid="quick-add-close" aria-label={t("action_cancel")}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-400 active:scale-95">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-xs text-slate-500" data-testid="quick-add-hint">{t("qa_hint")}</p>

        {/* Job context: current job remembered, 3 most recent offered first */}
        {!currentId && (
          <div className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="quick-add-jobs">
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <Building2 className="h-3.5 w-3.5" /> {t("qa_choose_job")}
            </div>
            <div className="flex flex-wrap gap-2">
              {recent.map((p) => (
                <button key={p.id} onClick={() => setPicked(p.id)} data-testid={`quick-add-job-${p.id}`}
                  className={`min-h-[40px] rounded-full border px-3 text-xs font-bold ${picked === p.id ? "border-brand bg-brand/10 text-brand" : "border-slate-200 bg-white text-slate-700"}`}>
                  {p.name}
                </button>
              ))}
              <button onClick={() => { onClose(); navigate("/app/projects"); }} data-testid="quick-add-job-all"
                className="min-h-[40px] rounded-full border border-dashed border-slate-300 px-3 text-xs font-bold text-slate-500">
                {t("qa_all_jobs")}
              </button>
            </div>
          </div>
        )}
        {currentId && (
          <div className="mb-3 rounded-2xl border border-brand/30 bg-brand/5 px-3 py-2 text-xs font-bold text-brand" data-testid="quick-add-context">
            {t("qa_adding_to")}: {projName(currentId) || t("qa_this_job")}
          </div>
        )}

        {!talk && !draft && (
          <div className="grid grid-cols-3 gap-2" data-testid="quick-add-grid">
            {actions.map((a) => (
              <button key={a.key} onClick={a.onClick} disabled={a.disabled} data-testid={a.testid}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 min-h-[72px] text-slate-700 active:scale-95 disabled:opacity-40">
                <a.icon className="h-5 w-5 text-brand" />
                <span className="text-xs font-semibold text-center leading-tight">{t(`qa_${a.key}`)}</span>
              </button>
            ))}
            <button onClick={() => setTalk(true)} data-testid="quick-add-talk"
              className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-brand bg-brand/10 p-4 min-h-[72px] text-brand active:scale-95">
              <Mic className="h-5 w-5" />
              <span className="text-xs font-bold text-center leading-tight">{t("qa_talk")}</span>
            </button>
          </div>
        )}

        {talk && !draft && (
          <div className="space-y-3" data-testid="quick-add-talk-panel">
            <p className="text-sm text-slate-600">{t("qa_talk_hint")}</p>
            <VoiceRecorder value={heard} onChange={(txt) => txt && parseVoice(txt)} lang={(i18n.language || "en").slice(0, 2)} />
            {busy && <div className="flex items-center justify-center gap-2 py-2 text-sm text-brand"><Loader2 className="h-4 w-4 animate-spin" /> {t("qa_thinking")}</div>}
            <button onClick={() => { setTalk(false); setHeard(""); }} data-testid="quick-add-talk-back"
              className="w-full min-h-[44px] rounded-full border border-slate-300 text-sm font-bold text-slate-600">{t("action_back")}</button>
          </div>
        )}

        {/* Preview card — Save only after one explicit tap (James's hard rule) */}
        {draft && (
          <div className="space-y-3" data-testid="quick-add-preview">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-brand">
                {DraftIcon && <DraftIcon className="h-4 w-4" />} {t(`qa_${draft.kind}`)}
              </div>
              {(draft.kind === "note" || draft.kind === "task") && !heard ? (
                <input autoFocus value={draft.title} data-testid="quick-add-title"
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder={t(draft.kind === "note" ? "qa_note_ph" : "qa_task_ph")}
                  className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" />
              ) : (
                <div className="mt-2 text-sm font-semibold text-slate-900" data-testid="quick-add-draft-title">{draft.title}</div>
              )}
              {heard && <div className="mt-1 text-xs italic text-slate-500" data-testid="quick-add-heard">“{heard}”</div>}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 font-semibold" data-testid="quick-add-draft-job">
                  {projName(draft.project_id) || t("qa_choose_job")}
                </span>
                {draft.amount_cents != null && (
                  <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 font-semibold" data-testid="quick-add-draft-amount">
                    {fmtMoney(draft.amount_cents)}
                  </span>
                )}
                {draft.due_date && (
                  <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 font-semibold" data-testid="quick-add-draft-due">{draft.due_date}</span>
                )}
              </div>
            </div>
            <button onClick={saveDraft} disabled={busy || !draft.project_id || !(draft.title || "").trim()} data-testid="quick-add-save"
              className="flex w-full min-h-[52px] items-center justify-center gap-2 rounded-full bg-brand text-base font-bold text-white active:scale-95 disabled:opacity-40">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("action_save")}
            </button>
            <button onClick={() => { setDraft(null); setHeard(""); }} data-testid="quick-add-preview-back"
              className="w-full min-h-[44px] rounded-full border border-slate-300 text-sm font-bold text-slate-600">{t("action_back")}</button>
          </div>
        )}
      </div>
    </div>, document.body);
}
